import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { RootState } from '@mindfulchefuk/types/store'
import { useRouter } from 'next/router'

import {
  logAddRecipeToCart,
  logRemoveRecipeFromCart,
} from '@mindfulchefuk/utils/analytics'
import {
  logCheckoutStart,
  logSocialCheckoutStart,
} from '@mindfulchefuk/features/Onboarding/utils/onboardingAnalytics'
import { updateBasket } from '@mindfulchefuk/actions/basketActions'
import routesConfig from '@mindfulchefuk/config/routesConfig'
import { RecipeWithQuantity } from '@mindfulchefuk/features/Recipes/interfaces'
import { ProductType } from '@mindfulchefuk/analytics'
import { useIsNewCustomer } from '@mindfulchefuk/features/Onboarding/hooks/useIsNewCustomer'

import {
  logOnboardingConfirmRecipesFromBasket,
  logOnboardingDecrementRecipeFromBasket,
  logOnboardingIncrementRecipeFromBasket,
} from '@mindfulchefuk/utils/analytics/ga/onboardingRecipeBasket'
import { trackAnalyticsEvent } from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'

type ClickOrigination = 'modal' | 'recipe' | 'basket'

export const useDeprecatedChooseRecipesActions = () => {
  const dispatch = useDispatch()
  const router = useRouter()
  const { isNewCustomer } = useIsNewCustomer()

  const { items, total, totalWithDiscount } = useSelector(
    (store: RootState) => ({
      items: store.basket.items,
      total: store.basket.total,
      totalWithDiscount: store.basket.totalWithDiscount,
    }),
    shallowEqual
  )

  const handleAddRecipe =
    (clickOrigination: ClickOrigination) => (recipe: RecipeWithQuantity) => {
      dispatch(updateBasket(recipe, 'add'))
      logAddRecipeToCart(recipe)

      if (clickOrigination === 'basket') {
        logOnboardingIncrementRecipeFromBasket()
      }

      trackAnalyticsEvent({
        name: 'product added',
        data: {
          'product id': recipe.id,
          'product name': recipe.title,
          'product type': 'recipe',
          'product category': ProductType.RECIPES,
          'product sub-category': recipe.cuisine,
          'product price': recipe.price,
          'recipe portion count': recipe.portions,
          'product quantity': 1,
          'page source':
            clickOrigination === 'modal' ? 'recipes details' : null,
          'food group': recipe.foodGroup,
          rawPageSource: sessionStorage.getItem('referrerPath'),
        },
      })
    }

  const handleClickFromModal = handleAddRecipe('modal')
  const handleClickFromRecipeCard = handleAddRecipe('recipe')
  const handleIncrementRecipeFromBasket = handleAddRecipe('basket')

  const handleConfirmClick = () => {
    logCheckoutStart({ items, price: total, totalWithDiscount })
    logSocialCheckoutStart()

    if (isNewCustomer) {
      return router.push(routesConfig.postLoginOnboardingShipping.pathname)
    }
    router.push(routesConfig.aboutYou.pathname)
  }

  const handleConfirmClickFromTheBasket = () => {
    logOnboardingConfirmRecipesFromBasket()
    handleConfirmClick()
  }

  const handleRemoveRecipe =
    (clickOrigination: ClickOrigination) => (recipe: RecipeWithQuantity) => {
      dispatch(updateBasket(recipe, 'remove'))
      trackAnalyticsEvent({
        name: 'product removed',
        data: {
          'product id': recipe.id,
          'product name': recipe.title,
          'product type': 'recipe',
          'product category': ProductType.RECIPES,
          'product sub-category': recipe.cuisine,
          'product price': recipe.price,
          'recipe portion count': recipe.portions,
          'product quantity': 1,
          'page source':
            clickOrigination === 'modal' ? 'recipes details' : null,
          'food group': recipe.foodGroup,
          rawPageSource: sessionStorage.getItem('referrerPath'),
        },
      })
      logRemoveRecipeFromCart(recipe)
    }

  const handleDecrementRecipeFromBasket = (recipe: RecipeWithQuantity) => {
    logOnboardingDecrementRecipeFromBasket()
    handleRemoveRecipe('basket')(recipe)
  }

  return {
    handleClickFromModal,
    handleClickFromRecipeCard,
    handleConfirmClick,
    handleConfirmClickFromTheBasket,
    handleRemoveRecipe,
    handleIncrementRecipeFromBasket,
    handleDecrementRecipeFromBasket,
  }
}
